import {
  useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import JSZip from 'jszip';

import useFocusedModule from 'hooks/useFocusedModule';
import useLanguage from 'hooks/useLanguage';
import { Mixpanel } from 'mixpanel';
import useFile from 'api/hooks/useFile';
import useFileBrief from 'api/hooks/useFileBrief';
import useUser from 'api/hooks/useUser';
import useFlowCharts from 'api/hooks/useFlowCharts';
import useFigures from 'api/hooks/useFugures';
import ApplicationBriefResult from 'components/ApplicationBriefResult';
import ApplicationIndustryInitial from 'components/ApplicationIndustryInitial';
import ApplicationIndustryProduct from 'components/ApplicationIndustryProduct';
import { IData } from 'features/ProductConverter';
import { cleanString, unMaskInput } from 'utils/helpers';
import { AlkaliProps } from 'utils/types';

export interface BriefInitialProps {
  watchCompanyName?: string;
  industry: string;
  subIndustry?: string;
  apps: string;
  products: string;
}

const ApplicationBrief = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { data } = useFile({ type: 'converter' });
  const { data: brief } = useFileBrief();
  const [productAdd, setProductAdd] = useState<BriefInitialProps | null>(null);
  const [comparisonData, setComparisonData] = useState<IData | null>(null);
  const [isProductAdd, setIsProductAdd] = useState<boolean>(false);
  const [generatedBrief, setGeneretedBrief] = useState<boolean>(false);
  const [filesCharts, setFilesCharts] = useState<JSZip.JSZipObject[]>([]);
  const [filesFigures, setFilesFigures] = useState<JSZip.JSZipObject[]>([]);

  const { data: user } = useUser();
  const { blueLang } = useLanguage();
  const { data: flowCharts } = useFlowCharts();
  const { data: figures } = useFigures();

  const parseZip = async (buf: ArrayBuffer) => {
    try {
      const zip = await new JSZip().loadAsync(buf);

      return Object.values(zip.files);
    } catch (err) {
      return null;
    }
  };

  useEffect(() => {
    parseZip(figures).then((data) => {
      setFilesFigures(data || []);
    });
  }, [figures]);

  useEffect(() => {
    parseZip(flowCharts).then((data) => {
      setFilesCharts(data || []);
    });
  }, [flowCharts]);

  useFocusedModule('Application Brief Module Duration');

  useEffect(() => {
    if (user) {
      Mixpanel.track('Application Brief Opened', {
        role: user?.role?.name,
        organization: user?.organization,
      });
    }
  }, [user]);

  useEffect(() => {
    if (blueLang.length > 0) {
      i18n.changeLanguage(blueLang);
    }
  }, [blueLang]);

  const filteredData = useMemo(() => {
    const omyaData: AlkaliProps[] = [];
    const productData: AlkaliProps[] = [];
    data?.map((products) => {
      if (products['Material A'].includes('Omya')) {
        omyaData.push(products);
      } else {
        productData.push(products);
      }
    });

    return { omyaData,
      productData };
  }, [data]);

  const expectedProductConsumption = useMemo(() => {
    if (!comparisonData?.alkali) {
      return 0;
    }

    const currentAlkali = filteredData?.productData?.find(
      (product) => cleanString(product['Material A']) ===
          cleanString(comparisonData?.alkali) &&
        product?.Concentration === Number(comparisonData?.concentration),
    );

    const currentOmya = filteredData?.omyaData?.find((product) => {
      if (comparisonData?.omya) {
        return (
          cleanString(product['Material A']).includes(
            cleanString(comparisonData?.omya),
          ) &&
          product?.Concentration === Number(comparisonData?.concentrationOmya)
        );
      }
    });

    if (
      !currentAlkali ||
      !currentOmya ||
      !comparisonData?.consumption ||
      !comparisonData?.concentration ||
      !comparisonData?.concentrationOmya
    ) {
      return 0;
    }

    const alkaliConsumption = Number(unMaskInput(comparisonData?.consumption));
    const alkaliEq =
      currentAlkali?.Stoichiometry / currentAlkali?.['Mol. Weight'];

    const alkaliConcentration = Number(comparisonData?.concentration);
    const alkaliValue = alkaliConsumption * alkaliEq * alkaliConcentration;

    const omyaEq = currentOmya?.Stoichiometry / currentOmya?.['Mol. Weight'];

    const omyaConcentration = Number(comparisonData?.concentrationOmya);

    const omyaValue = omyaEq * omyaConcentration;

    return alkaliValue / omyaValue;
  }, [comparisonData, filteredData]);

  return (
    <div className="px-[100px] py-[32px] md:px-[16px] md:pb-4">
      <div className="mb-8 md:mb-4">
        <span
          onClick={() => navigate('/')}
          className="text-secondaryText cursor-pointer"
        >
          {t('home')} /
        </span>{' '}
        <span className="font-medium">{t('industryBrief')}</span>
      </div>
      <div>
        <div className={`${!generatedBrief && !isProductAdd ? '' : 'hidden'}`}>
          <div>
            <ApplicationIndustryInitial
              briefData={brief ? brief : []}
              setProductAdd={setProductAdd}
              setIsProductAdd={setIsProductAdd}
              setGeneretedBrief={setGeneretedBrief}
              setComparisonData={setComparisonData}
            />
          </div>
        </div>
        <div className={`${generatedBrief && data ? '' : 'hidden'}`}>
          <ApplicationBriefResult
            comparisonData={comparisonData}
            product={productAdd}
            brief={brief}
            expectedConsumption={Math.round(expectedProductConsumption)}
            setGeneretedBrief={setGeneretedBrief}
            flowCharts={filesCharts}
            figures={filesFigures}
          />
        </div>
        <div
          className={`${
            !generatedBrief && isProductAdd && data ? '' : 'hidden'
          }`}
        >
          <ApplicationIndustryProduct
            data={filteredData?.productData}
            omyaData={filteredData?.omyaData}
            setComparisonData={setComparisonData}
            back={() => setIsProductAdd(false)}
            product={productAdd?.products}
            setGeneretedBrief={setGeneretedBrief}
          />
        </div>
      </div>
    </div>
  );
};

export default ApplicationBrief;
